/**
 * Outsourcing contract entity adapter (P338).
 *
 * The first-class contract register the SAMA field examiner reads
 * (F5: "46 contracts, all 46 critical") -- criticality must
 * discriminate; Material outsourcing carries SAMA section-J
 * no-objection evidence. P15 admin scaffolding over
 * /v1/admin/foundation/outsourcing-contracts.
 */
import { i18n } from "shared/i18n";
import type { AdminColumn, EntityAdapter, FormField } from "./types";
import {
  ContractServiceLinks,
  ContractSubcontractorLinks,
} from "./outsourcingContractLinks";

export type ContractCriticality = "critical" | "high" | "medium" | "low";

const CRITICALITY_TONE: Record<ContractCriticality, string> = {
  critical: "bg-rose-100 text-rose-900",
  high: "bg-amber-100 text-amber-900",
  medium: "bg-sky-100 text-sky-900",
  low: "bg-slate-100 text-slate-700",
};

export interface OutsourcingContract {
  /** Round 20 V7. */
  services_not_supplied?: string[];
  id: string;
  contract_ref: string;
  title: string;
  vendor_id: string;
  criticality: ContractCriticality;
  material_outsourcing: boolean;
  sama_no_objection_ref: string | null;
  sama_no_objection_date: string | null;
  start_date: string | null;
  end_date: string | null;
  next_review_date: string | null;
  exit_strategy_summary: string | null;
  subcontractors: string | null;
  data_location: string | null;
  annual_value_sar: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface OutsourcingContractIn {
  contract_ref: string;
  title: string;
  vendor_id: string | null;
  criticality: ContractCriticality;
  material_outsourcing: boolean;
  sama_no_objection_ref: string | null;
  sama_no_objection_date: string | null;
  start_date: string | null;
  end_date: string | null;
  next_review_date: string | null;
  exit_strategy_summary: string | null;
  subcontractors: string | null;
  data_location: string | null;
  annual_value_sar: string | null;
  notes: string | null;
}

export type OutsourcingContractPatch = Partial<OutsourcingContractIn>;

const columns: AdminColumn<OutsourcingContract>[] = [
  {
    key: "contract_ref",
    label: "Ref",
    sortValue: (r) => r.contract_ref,
    render: (r) => (
      <span className="font-mono text-xs text-slate-700">{r.contract_ref}</span>
    ),
  },
  {
    key: "title",
    label: "Title",
    sortValue: (r) => r.title,
    render: (r) => (
      <span className="font-medium text-slate-900">
        {r.title}
        {/* Round 20 V7 -- services this contract names that its supplier
            is not linked to on the vendor register. */}
        {(r.services_not_supplied ?? []).length > 0 && (
          <span
            data-testid="contract-register-unsupplied"
            className="ms-2 block text-[11px] font-normal text-rose-700"
          >
            {i18n.t("supplierCoherence.contractRow", {
              services: (r.services_not_supplied ?? []).join(", "),
            })}
          </span>
        )}
      </span>
    ),
  },
  {
    key: "criticality",
    label: "Criticality",
    sortValue: (r) => r.criticality,
    render: (r) => (
      <span
        data-testid={`contract-criticality-${r.criticality}`}
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${CRITICALITY_TONE[r.criticality]}`}
      >
        {r.criticality}
      </span>
    ),
  },
  {
    key: "material",
    label: "Material",
    sortValue: (r) => (r.material_outsourcing ? 0 : 1),
    render: (r) =>
      r.material_outsourcing ? (
        r.sama_no_objection_ref || r.sama_no_objection_date ? (
          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900">
            {i18n.t("screens.outsourcingContractAdapter.materialNocOnFile")}
          </span>
        ) : (
          <span
            data-testid="contract-material-no-noc"
            className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-900"
          >
            {i18n.t("screens.outsourcingContractAdapter.materialNoSamaNoc")}
          </span>
        )
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
  {
    key: "end_date",
    label: "Ends",
    sortValue: (r) => r.end_date ?? "",
    render: (r) =>
      r.end_date ? (
        <span className="text-xs text-slate-600">{r.end_date}</span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
];

const formFields: FormField<OutsourcingContractIn>[] = [
  {
    kind: "text",
    key: "contract_ref",
    label: "Contract reference",
    required: true,
    maxLength: 64,
  },
  { kind: "text", key: "title", label: "Title", required: true, maxLength: 255 },
  {
    kind: "entityPicker",
    key: "vendor_id",
    label: "Vendor (third party)",
    required: true,
    entityKind: "vendor",
  },
  {
    kind: "select",
    key: "criticality",
    label: "Criticality",
    required: true,
    options: (["critical", "high", "medium", "low"] as const).map((v) => ({
      value: v,
      label: v,
    })),
  },
  {
    kind: "bool",
    key: "material_outsourcing",
    label: "Material outsourcing (SAMA no-objection required)",
  },
  {
    kind: "text",
    key: "sama_no_objection_ref",
    label: "SAMA no-objection ref",
    maxLength: 128,
    group: "SAMA no-objection (Material only)",
  },
  {
    kind: "date",
    key: "sama_no_objection_date",
    label: "SAMA no-objection date",
    group: "SAMA no-objection (Material only)",
  },
  { kind: "date", key: "start_date", label: "Start date", group: "Term" },
  { kind: "date", key: "end_date", label: "End date", group: "Term" },
  {
    kind: "date",
    key: "next_review_date",
    label: "Next review date",
    group: "Term",
  },
  {
    kind: "textarea",
    key: "exit_strategy_summary",
    label: "Exit strategy summary",
    rows: 3,
    group: "Resilience depth",
  },
  {
    kind: "textarea",
    key: "subcontractors",
    label: "Subcontractors (fourth parties)",
    rows: 2,
    group: "Resilience depth",
  },
  {
    kind: "text",
    key: "data_location",
    label: "Data location (residency)",
    maxLength: 128,
    group: "Resilience depth",
  },
  {
    kind: "text",
    key: "annual_value_sar",
    label: "Annual value (SAR)",
    group: "Resilience depth",
  },
  { kind: "textarea", key: "notes", label: "Notes", rows: 2 },
];

export const outsourcingContractAdapter: EntityAdapter<
  OutsourcingContract,
  OutsourcingContractIn,
  OutsourcingContractPatch
> = {
  i18nPrefix: "modules.foundation.admin.outsourcingContracts",
  importKind: "outsourcing_contract",
  endpoint: "/v1/admin/foundation/outsourcing-contracts",
  queryKey: ["foundation-admin", "outsourcing-contracts"] as const,
  columns,
  defaultSort: { key: "criticality", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) =>
    `${r.contract_ref} ${r.title} ${r.criticality} ${r.data_location ?? ""} ${r.subcontractors ?? ""}`,
  formFields,
  formInitial: () => ({
    contract_ref: "",
    title: "",
    vendor_id: null,
    criticality: "medium",
    material_outsourcing: false,
    sama_no_objection_ref: null,
    sama_no_objection_date: null,
    start_date: null,
    end_date: null,
    next_review_date: null,
    exit_strategy_summary: null,
    subcontractors: null,
    data_location: null,
    annual_value_sar: null,
    notes: null,
  }),
  extraSections: [
    ({ rowId }) => <ContractServiceLinks rowId={rowId} />,
    ({ rowId }) => <ContractSubcontractorLinks rowId={rowId} />,
  ],
  rowToPatch: (r) => ({
    contract_ref: r.contract_ref,
    title: r.title,
    vendor_id: r.vendor_id,
    criticality: r.criticality,
    material_outsourcing: r.material_outsourcing,
    sama_no_objection_ref: r.sama_no_objection_ref,
    sama_no_objection_date: r.sama_no_objection_date,
    start_date: r.start_date,
    end_date: r.end_date,
    next_review_date: r.next_review_date,
    exit_strategy_summary: r.exit_strategy_summary,
    subcontractors: r.subcontractors,
    data_location: r.data_location,
    annual_value_sar: r.annual_value_sar,
    notes: r.notes,
  }),
};
